import { cn } from '@/lib/utils';

import { WithRef } from '@/cms/types/types';

export interface RadioProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  classNames?: {
    root?: string;
  };
  label?: string;
  isDisabled?: boolean;
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const commonStyles = cn(
  'peer flex items-center h-4 w-4 border-[1.5px] bg-transparent  cursor-pointer rounded-3xl appearance-none'
);

const getBgColor = (isDisabled: boolean | undefined) => {
  if (isDisabled) return 'bg-gray-100';

  return 'bg-base-white';
};

const getBorderColor = (
  isDisabled: boolean | undefined,
  checked: boolean | undefined
) => {
  if (isDisabled && checked) return 'border-gray-400 hover:border-gray-500';
  if (isDisabled && !checked) return 'border-gray-300 hover:border-gray-300';
  if (!isDisabled && checked) return 'border-brand-500 hover:border-brand-600';
  return 'border-gray-400 hover:border-gray-500';
};
export const Radio = ({
  classNames,
  label,
  value,
  onChange,
  isDisabled,
  ref,
  checked,
  id,
  ...props
}: Omit<WithRef<HTMLInputElement, RadioProps>, 'disabled'>) => {
  const inputId = `${props.name}-${id}`;

  const conditionalClasses = cn(
    commonStyles,
    getBorderColor(isDisabled, checked),
    getBgColor(isDisabled),
    classNames?.root
  );

  return (
    <div className='relative flex items-center gap-x-1.5 '>
      <input
        type='radio'
        className={conditionalClasses}
        disabled={isDisabled}
        value={value}
        onChange={onChange}
        ref={ref}
        id={inputId}
        checked={checked}
        {...props}
      />
      <div
        className={cn(
          'pointer-events-none absolute left-1 h-2 w-2 rounded-3xl',
          isDisabled
            ? 'peer-checked:bg-gray-400'
            : 'peer-checked:bg-brand-500 peer-checked:hover:bg-brand-600'
        )}
      />
      <label
        htmlFor={inputId}
        className={cn(
          'text-body-m font-mono',
          isDisabled && checked && 'text-gray-500',
          isDisabled && !checked && 'text-gray-400',
          !isDisabled && 'text-gray-950'
        )}
      >
        {label}
      </label>
    </div>
  );
};
